import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import {
  Download,
  ExternalLink,
  File,
  FileCode2,
  FileImage,
  FileText,
  Film,
  Globe2,
  Loader2,
  Music2,
  PanelRightClose,
  Presentation,
  RefreshCw,
  TriangleAlert,
  X,
} from "lucide-react";
import type {
  ComponentType,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
} from "react";

import { useMonkeysLocaleMessages } from "../../provider";
import { cn } from "../layout";
import { Button } from "../primitives";
import {
  type AgentWorkbenchAppearanceOptions,
  useAgentWorkbenchAppearance,
} from "./appearance";
import type {
  AgentWorkbenchArtifactItem,
  AgentWorkbenchArtifactKind,
  AgentWorkbenchArtifactWorkspaceIntent,
} from "./artifact-model";

export interface AgentWorkbenchArtifactWorkspaceProps
  extends AgentWorkbenchAppearanceOptions {
  artifacts: readonly AgentWorkbenchArtifactItem[];
  activeArtifactId?: string;
  className?: string;
  renderArtifact: (artifact: AgentWorkbenchArtifactItem) => ReactNode;
  onIntent?: (intent: AgentWorkbenchArtifactWorkspaceIntent) => void;
}

const ARTIFACT_ICONS: Record<
  AgentWorkbenchArtifactKind,
  ComponentType<{ className?: string }>
> = {
  audio: Music2,
  file: File,
  html: FileCode2,
  image: FileImage,
  markdown: FileText,
  pdf: FileText,
  presentation: Presentation,
  video: Film,
  web: Globe2,
};

function SortableArtifactTab({
  artifact,
  selected,
  onClose,
}: {
  artifact: AgentWorkbenchArtifactItem;
  selected: boolean;
  onClose: () => void;
}) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.artifactWorkspace;
  const Icon = ARTIFACT_ICONS[artifact.kind];
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: artifact.id,
  });
  const {
    role: _sortableRole,
    tabIndex: _sortableTabIndex,
    ...sortableA11y
  } = attributes;
  const setTabRef = (node: HTMLButtonElement | null) => {
    setNodeRef(node);
    setActivatorNodeRef(node);
  };
  const handleMouseDown = (event: MouseEvent<HTMLButtonElement>) => {
    if (event.button !== 1) return;
    event.preventDefault();
    onClose();
  };
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (
      !(event.target instanceof Element) ||
      !event.target.closest("[data-close-artifact-tab]")
    )
      return;
    event.preventDefault();
    onClose();
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    listeners?.onKeyDown?.(event);
    if (event.key !== "Delete" && event.key !== "Backspace") return;
    event.preventDefault();
    onClose();
  };

  return (
    <TabsPrimitive.Trigger
      ref={setTabRef}
      value={artifact.id}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "group/tab relative flex h-9 max-w-56 shrink-0 items-center gap-2 rounded-lg pl-3 pr-8 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring",
        selected
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:bg-background/60",
        isDragging && "opacity-60",
      )}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      {...sortableA11y}
      {...listeners}
      onKeyDown={handleKeyDown}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      <span className="truncate">{artifact.name}</span>
      <span
        className="absolute right-1.5 flex size-6 items-center justify-center rounded-md opacity-0 hover:bg-muted group-focus/tab:opacity-100 group-hover/tab:opacity-100 data-[selected=true]:opacity-100"
        data-selected={selected}
        data-close-artifact-tab=""
        title={messages.closeTab({ name: artifact.name })}
        aria-hidden="true"
      >
        <X className="size-3.5" aria-hidden="true" />
      </span>
    </TabsPrimitive.Trigger>
  );
}

export function AgentWorkbenchArtifactWorkspace({
  artifacts,
  activeArtifactId,
  className,
  variant,
  density,
  renderArtifact,
  onIntent,
}: AgentWorkbenchArtifactWorkspaceProps) {
  const appearance = useAgentWorkbenchAppearance({ variant, density });
  const compact = appearance.density === "compact";
  const messages = useMonkeysLocaleMessages().agentWorkbench.artifactWorkspace;
  const activeArtifact =
    artifacts.find((artifact) => artifact.id === activeArtifactId) ??
    artifacts[0];
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  if (!activeArtifact) return null;

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onIntent?.({
      type: "reorder",
      artifactId: String(active.id),
      overArtifactId: String(over.id),
    });
  };

  const artifactContent =
    activeArtifact.status === "loading" ? (
      <div
        className="flex size-full min-h-48 items-center justify-center p-6 text-center"
        role="status"
      >
        <div className="flex min-w-0 flex-col items-center gap-3 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" aria-hidden="true" />
          <span>{messages.loading}</span>
        </div>
      </div>
    ) : activeArtifact.status === "error" ? (
      <div
        className="flex size-full min-h-48 items-center justify-center p-6 text-center"
        role="alert"
      >
        <div className="flex min-w-0 max-w-sm flex-col items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-full bg-destructive/10 text-destructive-text">
            <TriangleAlert className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <div className="break-words text-sm font-medium text-foreground [overflow-wrap:anywhere]">
              {messages.failed}
            </div>
            {activeArtifact.description ? (
              <p className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap break-words text-sm text-muted-foreground [overflow-wrap:anywhere]">
                {activeArtifact.description}
              </p>
            ) : null}
          </div>
          {onIntent ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={() =>
                onIntent({ type: "retry", artifactId: activeArtifact.id })
              }
            >
              <RefreshCw className="size-4" aria-hidden="true" />
              {messages.retry}
            </Button>
          ) : null}
        </div>
      </div>
    ) : (
      renderArtifact(activeArtifact)
    );

  return (
    <TabsPrimitive.Root
      value={activeArtifact.id}
      onValueChange={(artifactId) => onIntent?.({ type: "select", artifactId })}
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      data-agent-workbench-status={activeArtifact.status}
      data-agent-workbench-surface="panel"
      className={cn(
        "flex size-full min-h-0 min-w-0 flex-col bg-card text-card-foreground",
        className,
      )}
      orientation="horizontal"
    >
      <div
        className={cn(
          "relative z-10 flex shrink-0 items-center gap-2 border-b border-border bg-muted/40 px-2",
          compact ? "h-10" : "h-12",
        )}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={artifacts.map((artifact) => artifact.id)}
            strategy={horizontalListSortingStrategy}
          >
            <TabsPrimitive.List
              className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              aria-label={messages.tabs}
            >
              {artifacts.map((artifact) => (
                <SortableArtifactTab
                  key={artifact.id}
                  artifact={artifact}
                  selected={artifact.id === activeArtifact.id}
                  onClose={() =>
                    onIntent?.({ type: "close", artifactId: artifact.id })
                  }
                />
              ))}
            </TabsPrimitive.List>
          </SortableContext>
        </DndContext>
        <div className="relative z-20 flex shrink-0 items-center gap-2">
          {activeArtifact.url ? (
            <>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={messages.download({ name: activeArtifact.name })}
                onClick={() =>
                  onIntent?.({
                    type: "download",
                    artifactId: activeArtifact.id,
                  })
                }
              >
                <Download className="size-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
                aria-label={messages.openExternal({
                  name: activeArtifact.name,
                })}
                onClick={() =>
                  onIntent?.({
                    type: "open-external",
                    artifactId: activeArtifact.id,
                  })
                }
              >
                <ExternalLink className="size-4" aria-hidden="true" />
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={messages.closeWorkspace}
            onClick={() => onIntent?.({ type: "close-workspace" })}
          >
            <PanelRightClose className="size-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
        {artifacts.map((artifact) => (
          <TabsPrimitive.Content
            key={artifact.id}
            value={artifact.id}
            className="size-full min-h-0 min-w-0 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {artifact.id === activeArtifact.id ? artifactContent : null}
          </TabsPrimitive.Content>
        ))}
      </div>
    </TabsPrimitive.Root>
  );
}
